/**
 * ApiKeyTablePage - Group API key management using TablePage component
 */

import TablePage from '@core/pages/TablePage.js';
import { ApiKey, ApiKeyList, ApiKeyForms } from '@core/models/ApiKey.js';
import ApiKeyView from './ApiKeyView.js';

// Register the add/edit forms on the model class so TableView can find them automatically
ApiKey.ADD_FORM = ApiKeyForms.create;
ApiKey.EDIT_FORM = ApiKeyForms.edit;

class ApiKeyTablePage extends TablePage {
    constructor(options = {}) {
        super({
            ...options,
            name: 'admin_api_keys',
            pageName: 'API Keys',
            router: 'admin/api-keys',
            Collection: ApiKeyList,

            itemViewClass: ApiKeyView,
            // ApiKeyView is a DetailView: it renders its own header (with the
            // × close) and needs the flush body — same trio every sibling
            // DetailView-based admin table passes (ITEM-025).
            viewDialogOptions: {
                header: false,
                size: 'lg',
                noBodyPadding: true,
                buttons: []
            },

            columns: [
                { key: 'id', label: 'ID', width: '70px', sortable: true, class: 'text-muted' },
                { key: 'name', label: 'Name', sortable: true },
                { key: 'group.name', label: 'Group', sortable: true, formatter: "default('—')" },
                {
                    key: 'is_active',
                    label: 'Status',
                    formatter: "boolean('Active|bg-success','Inactive|bg-secondary')|badge",
                    width: '100px'
                },
                { key: 'created', label: 'Created', formatter: 'datetime', sortable: true }
            ],

            selectable: true,
            searchable: true,
            searchPlaceholder: 'Search name or group',
            sortable: true,
            filterable: true,
            paginated: true,

            showRefresh: true,
            showAdd: true,
            showExport: false,

            addButtonLabel: 'New API Key',

            emptyMessage: 'No API keys found.',

            tableOptions: {
                striped: true,
                bordered: false,
                hover: true,
                responsive: false
            }
        });
    }

    // Override to intercept and show the one-time token after model.save()
    async onActionAdd() {
        const app = this.getApp();
        const model = new ApiKey();
        const result = await app.showForm({
            model,
            ...ApiKeyForms.create
        });
        if (!result) return;

        // Grant-only create: drop unchecked permission switches so the POST
        // carries one dotted `permissions.<name>: true` key per granted
        // permission and nothing else (absent = not granted; explicit falses
        // would fire the backend's per-key permission gate for nothing).
        const payload = Object.fromEntries(
            Object.entries(result).filter(([k, v]) => !k.startsWith('permissions.') || v === true)
        );

        const resp = await model.save(payload);
        if (!resp?.data?.status) {
            app.showError(resp?.data?.error || 'Failed to create API key');
            return;
        }

        // Token is only present in the creation response — show it once
        const token = resp.data?.data?.token;
        await app.showAlert({
            title: 'API Key Created — Save Your Token',
            message: token
                ? `Copy this token now. It will not be shown again.\n\n${token}`
                : 'API key created successfully.',
            type: token ? 'warning' : 'success',
            size: 'lg'
        });

        this.collection.add(model);
        this.tableView?.refresh();
    }
}

export default ApiKeyTablePage;
